import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, PlusCircle } from "lucide-react";
import {
  Box,
  Button,
  Container,
  IconButton,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ICPResearchDetailView from "./ICPResearchDetailView";
import { ICPProvider, useICP } from "./ICPProvider";
import SearchAndFilters from "../../../components/SearchAndFilters";
import SearchContentContainer from "../../../components/SearchContentContainer";
import LoadingSkeleton from "../../../components/LoadingSkeleton";
import { ICP } from "../../../interfaces/ICP";
import { InitialSearchStates } from "../../../components/SearchAndFilters/searchStates";
import { filterConfigs } from "../../../components/SearchAndFilters/config";
import { GenerationProgress } from "../../../components/GenerationProgress";
import queryString from "query-string";
import { homeRoutePath, settingsPath } from "../../Router";
import SessionService from "../../../services/Session";
import { useDispatch } from "react-redux";
import { setUser } from "../../../redux/actions/user";
import { logIssueNetworkRequestError } from "../../../utils/Loggers";
import useWebSocket from "../../../utils/Sockets";
import { useSubscriptionLimit } from "../../../hooks/useSubscriptionLimit";
import SubscriptionLimitDialog from "../../../components/SubscriptionLimitDialog";
import { displayToastMessage } from "../../../utils/Toasts";
import StartResearchDialog from "./StartResearchDialog";

const ICPCard = ({ icp, onClick, index }: any) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      onClick={() => onClick(icp)}
    >
      <Card
        className="h-full hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
        sx={{
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.08)",
          backgroundColor: "white",
          cursor: "pointer",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
            borderColor: "rgba(225, 29, 72, 0.2)",
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2.5,
              alignItems: "center",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
              <Typography
                variant="h6"
                className="mb-1 line-clamp-1"
                sx={{
                  fontWeight: 600,
                  fontSize: "1.125rem",
                  color: "#111827",
                }}
              >
                {icp.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#6B7280",
                  fontSize: "0.875rem",
                }}
              >
                Generated by {icp.metadata?.generatedBy}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 2.5, mt: 1 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#6B7280",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  mb: 0.5,
                }}
              >
                Target Industries
              </Typography>
              <Typography
                className="line-clamp-1"
                sx={{
                  fontSize: "0.875rem",
                  color: "#111827",
                }}
              >
                {icp.targeting?.industry?.sectors?.join(", ")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#6B7280",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  mb: 0.5,
                }}
              >
                Key Decision Makers
              </Typography>
              <Typography
                className="line-clamp-1"
                sx={{
                  fontSize: "0.875rem",
                  color: "#111827",
                }}
              >
                {icp.targeting?.professional?.titles?.join(", ")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#6B7280",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  mb: 0.5,
                }}
              >
                Conversion Timeline
              </Typography>
              <Typography
                className="line-clamp-1"
                sx={{
                  fontSize: "0.875rem",
                  color: "#111827",
                }}
              >
                {icp.conversion?.intent?.timeframe}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ICPList = ({ data, onICPSelect }: any) => {
  return (
    <Container maxWidth="xl">
      <Grid container spacing={3}>
        <AnimatePresence>
          {data.map((icp: ICP | any, index: number) => (
            <Grid item xs={12} md={6} key={icp.id || icp._id}>
              <ICPCard icp={icp} index={index} onClick={onICPSelect} />
            </Grid>
          ))}
        </AnimatePresence>
      </Grid>
    </Container>
  );
};

const ICPResearchViewDetails = ({ onBack }: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const [selectedICP, setSelectedICP] = useState<ICP | any>(null);
  const [searchState, setSearchState] = useState<any>(InitialSearchStates.icps);
  const [progress, setProgress] = useState(0);
  const [jobState, setJobState] = useState<any>(null);
  const [generationStage, setGenerationStage] = useState("");
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState("");
  const [generationError, setGenerationError] = useState("");
  const [showStartResearchDialog, setShowStartResearchDialog] = useState(false);

  const { dialogProps, makeRequest: makeSubscriptionGatedRequest } =
    useSubscriptionLimit(() => navigate(settingsPath, { replace: true }));

  const { socket } = useWebSocket({
    onProgress: (data) => {
      if (data.jobType === "icpResearch") {
        setProgress(data.progress);
        setGenerationStage(data.stage);
        setLastUpdateTimestamp(data.timestamp);
        setJobState("in-progress");
        if (data.progress === 100) {
          setJobState("completed");
        }
      }
    },
    onJobCompleted: (data) => {
      if (data.type === "icpResearch") {
        setProgress(100);
        setJobState("completed");
        setGenerationStage("Research Complete");
        setLastUpdateTimestamp(new Date().toISOString());
        fetchICPs(projectId, setSearchState);
      }
    },
    onJobFailed: (error) => {
      setProgress(-1);
      setJobState("error");
      setGenerationError(error.message);
      setLastUpdateTimestamp(new Date().toISOString());
    },
  });

  const { icps, loading, error, researchStatus, fetchICPs, startResearch } =
    useICP();

  const handleInitialLoad = useCallback(async () => {
    try {
      const parsedQuery = queryString.parse(location.search);
      if (parsedQuery.email && parsedQuery.view === "activate") {
        navigate(homeRoutePath, {
          replace: true,
        });
        return;
      }

      const sessionService = new SessionService();
      const sessionData = await sessionService.reviewSession();
      if (!sessionData.success) {
        navigate(homeRoutePath, { replace: true });
        return;
      }

      dispatch(setUser(sessionData.userSessionData));

      await fetchICPs(projectId, setSearchState);
    } catch (error: any) {
      logIssueNetworkRequestError("ICPResearchView.handleInitialLoad", error);
      navigate(homeRoutePath, { replace: true });
    }
  }, [location.search, navigate, dispatch, fetchICPs, projectId]);

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      if (mounted) {
        await handleInitialLoad();
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, []);

  const handleSearch = async (searchParams: any) => {
    setSearchState(searchParams);
    await fetchICPs(projectId, setSearchState, searchParams);
  };

  const handleStartResearch = useCallback(
    async (modelAPIKey: string | any) => {
      setJobState("initializing");
      setProgress(0);
      setGenerationStage("Starting Research");
      setLastUpdateTimestamp(new Date().toISOString());
      setGenerationError("");

      await makeSubscriptionGatedRequest({
        requestFn: () =>
          startResearch(projectId, {
            modelAPIKey,
            socketId: socket?.id,
            priority: 5,
          }),
        onSuccess: (started) => {
          if (!started) {
            setJobState("error");
            setGenerationError("Failed to start ICP research process");
            setProgress(-1);
            setLastUpdateTimestamp(new Date().toISOString());
          } else {
            displayToastMessage("success", "ICP research successfully started");
          }
        },
        onError: (error: any) => {
          console.error("Failed to start ICP research process", error);
          let errorMsg = error.error ? error.error : "";
          setJobState("error");
          setGenerationError(
            `Failed to start ICP research process. Please try again. Error: ${errorMsg}`
          );
          setProgress(-1);
          setLastUpdateTimestamp(new Date().toISOString());
        },
      });
    },
    [makeSubscriptionGatedRequest, projectId, socket?.id, startResearch]
  );

  const handleStartResearchDialogOpen = useCallback(() => {
    setShowStartResearchDialog(true);
    setGenerationError("");
  }, [setShowStartResearchDialog, setGenerationError]);

  const handleStartResearchDialogSubmit = useCallback(
    async (params: any) => {
      setShowStartResearchDialog(false);
      await handleStartResearch(params.modelAPIKey);
    },
    [setShowStartResearchDialog, handleStartResearch]
  );

  if (selectedICP) {
    return (
      <ICPResearchDetailView
        projectId={projectId}
        icp={selectedICP}
        onBack={() => setSelectedICP(null)}
      />
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: "#F9FAFB",
        borderRadius: "12px",
        px: { xs: 2, sm: 3, md: 4 },
        py: 4,
      }}
    >
      <Box
        sx={{
          mb: { xs: 3, sm: 4, md: 5 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 2, sm: 2 },
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: { xs: 1, sm: 0 },
          }}
        >
          <IconButton
            onClick={() => {
              if (onBack) onBack();
              navigate(`/project/${projectId}`);
            }}
            sx={{
              padding: { xs: 0.75, sm: 1 },
              backgroundColor: "white",
              border: "1px solid",
              borderColor: "rgba(0, 0, 0, 0.1)",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
              },
            }}
          >
            <ArrowLeft size={18} />
          </IconButton>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
              fontWeight: 600,
              color: "#111827",
            }}
          >
            ICP Research
          </Typography>
        </Box>
        <Box
          sx={{
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Button
            variant="contained"
            onClick={handleStartResearchDialogOpen}
            disabled={researchStatus?.status === "running"}
            startIcon={<PlusCircle size={18} />}
            sx={{
              width: { xs: "100%", sm: "auto" },
              padding: { xs: "10px 16px", sm: "10px 20px" },
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
              backgroundColor: "#E11D48",
              borderRadius: "8px",
              fontWeight: 500,
              boxShadow: "0 1px 3px rgba(225, 29, 72, 0.2)",
              "&:hover": {
                backgroundColor: "#BE123C",
                boxShadow: "0 2px 6px rgba(225, 29, 72, 0.3)",
              },
              textTransform: "none",
            }}
          >
            Start New Research
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: "8px",
            border: "1px solid #FEE2E2",
          }}
        >
          {error}
        </Alert>
      )}

      {jobState && (
        <Box sx={{ mb: 4 }}>
          <GenerationProgress
            title="Generating ICP Research"
            description="Please wait while we analyze your project data and generate ideal customer profiles..."
            progress={progress}
            stage={generationStage}
            timestamp={lastUpdateTimestamp}
            jobType="icpResearch"
            jobState={jobState}
            error={generationError}
          />
        </Box>
      )}

      <Card
        sx={{
          mb: 4,
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
          border: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.08)",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <SearchAndFilters
            totalShown={icps.length}
            onSearch={handleSearch}
            pagination={searchState.pagination}
            sorting={searchState.sorting}
            filters={searchState.filters}
            filterConfig={filterConfigs.icps}
            isLoading={loading}
          />
        </CardContent>
      </Card>

      <SearchContentContainer
        isLoading={loading}
        data={icps}
        LoadingComponent={LoadingSkeleton}
        ContentComponent={ICPList}
        componentProps={{
          onICPSelect: setSelectedICP,
        }}
        loadingText="Loading ICPs..."
        emptyTitle="No ICPs Found"
        emptyMessage="Try adjusting your search or filter criteria, or generate a new ICP research"
      />

      <StartResearchDialog
        open={showStartResearchDialog}
        onClose={() => setShowStartResearchDialog(false)}
        onSubmit={handleStartResearchDialogSubmit}
        loading={jobState === "initializing"}
        error={generationError}
      />

      <SubscriptionLimitDialog {...dialogProps} />
    </Box>
  );
};

const ICPResearchView = ({ onBack }: any) => {
  return (
    <ICPProvider>
      <ICPResearchViewDetails onBack={onBack} />
    </ICPProvider>
  );
};

export default ICPResearchView;
